/**
 * Onboarding flow — runs once after sign-up (email or Apple) before the user
 * lands on the home feed.
 *
 * Single-screen wizard. Pane 0 is ALWAYS the username step (claim/confirm your
 * handle — critical for Apple sign-ups). The username availability probe +
 * blank-screen/empty-200 guards are preserved verbatim.
 *
 * REVAMP (momentum pass):
 *   - NO SCROLLING anywhere: every step fits the viewport, down to iPhone SE
 *     (375×667). Sizes/paddings scale via useWindowDimensions — see `dims()`.
 *     The old ScrollView is gone; each pane is height-budgeted instead.
 *   - Everything is SKIPPABLE (including the username + final step) so momentum
 *     is never blocked. The primary CTA still gates on a valid username.
 *   - FINAL step is a full "Set up your profile" pane
 *     (components/onboarding/ProfileSetupPane): avatar + banner pickers,
 *     username (live availability), display name, Instagram + Facebook handles.
 *     It replaces the old avatar-only and "socials coming soon" panes.
 *   - Step order: username → interests ×3 → [wallet if SHOW_CRYPTO] →
 *     communities → profile setup.
 *
 * Finishing does NOT navigate imperatively: finish() flips
 * setOnboardingPending(false) and calls notifyAuthGate() — the useAuth() gate
 * then swaps the root tree from the Onboarding-only AuthStack to the full app.
 * This guarantees the user can't navigate back into the wizard (the stack is
 * unmounted). That contract is unchanged.
 *
 * react-native-video is loaded lazily so the screen stays tsc-clean +
 * crash-free before the lib is installed; when absent the interest-step video
 * header simply doesn't render.
 */
import { useState, useCallback, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  useWindowDimensions,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import CachedImage from '../components/ui/CachedImage';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LinearGradient from 'react-native-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '../components/ui/Icon';
import { colors, spacing, radius, fonts, getLayout, typographyScale } from '../constants/theme';
import { GlassSurface } from '../components/glass';
import ParticleBackground from '../components/ui/ParticleBackground';
import Asset3D from '../components/ui/Asset3D';
import ScreenHeading from '../components/ui/ScreenHeading';
import { tabularNums } from '../lib/format';
import {
  connectWalletAddress,
  joinCommunity,
  recommendCommunities,
  completeOnboarding,
  setOnboardingPending,
  getMe,
  validateUsername,
  checkUsernameAvailable,
  setUsername,
} from '../services/api';
import { notifyAuthGate } from '../navigation/useAuth';
import { SHOW_CRYPTO } from '../lib/comingSoon';
import ProfileSetupPane, { ProfileSetupHandle } from '../components/onboarding/ProfileSetupPane';

// react-native-video is loaded lazily so the screen stays tsc-clean + crash-free
// before the lib is installed. When present we render <RNVideo> behind the
// interest-step gradient; otherwise the header is a flat backdrop.
let RNVideo: any = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  RNVideo = require('react-native-video').default ?? require('react-native-video');
} catch {
  RNVideo = null;
}

// Onboarding videos (shipped in the binary). require() resolves at build time.
const VIDEOS = [
  require('../assets/onboarding/ob-1.mp4'),
  require('../assets/onboarding/ob-2.mp4'),
  require('../assets/onboarding/ob-3.mp4'),
];

// ── Interest steps (matches the original web onboarding; icons, NOT emojis) ──
interface Option { id: string; label: string; icon: string }
interface InterestStep { id: string; headline: string; sub: string; options: Option[] }

const INTEREST_STEPS: InterestStep[] = [
  {
    id: 'vibe',
    headline: 'What brings you here?',
    sub: "Pick everything that fits. We'll shape your experience around it.",
    options: [
      { id: 'creator', label: 'Creator', icon: 'color-palette-outline' },
      { id: 'investor', label: 'Investor', icon: 'trending-up-outline' },
      { id: 'builder', label: 'Builder', icon: 'construct-outline' },
      { id: 'collector', label: 'Collector', icon: 'albums-outline' },
      { id: 'explorer', label: 'Explorer', icon: 'compass-outline' },
      { id: 'networker', label: 'Networker', icon: 'people-outline' },
      { id: 'trader', label: 'Trader', icon: 'stats-chart-outline' },
      { id: 'learner', label: 'Learner', icon: 'book-outline' },
    ],
  },
  {
    id: 'interests',
    headline: 'What are you into?',
    sub: "Choose your topics. Mix freely — we'll find the overlap.",
    options: [
      { id: 'finance', label: 'Finance & Markets', icon: 'cash-outline' },
      { id: 'tech', label: 'Tech & Innovation', icon: 'hardware-chip-outline' },
      { id: 'art', label: 'Art & Design', icon: 'brush-outline' },
      { id: 'gaming', label: 'Gaming & Esports', icon: 'game-controller-outline' },
      { id: 'music', label: 'Music & Audio', icon: 'musical-notes-outline' },
      { id: 'sport', label: 'Sport & Fitness', icon: 'barbell-outline' },
      { id: 'science', label: 'Science & Research', icon: 'flask-outline' },
      { id: 'culture', label: 'Culture & Lifestyle', icon: 'sparkles-outline' },
      { id: 'business', label: 'Business & Startups', icon: 'briefcase-outline' },
      { id: 'wellness', label: 'Wellness & Mind', icon: 'heart-outline' },
      { id: 'food', label: 'Food & Travel', icon: 'restaurant-outline' },
      { id: 'crypto', label: 'Crypto & Web3', icon: 'logo-bitcoin' },
    ],
  },
  {
    id: 'style',
    headline: 'How do you like to engage?',
    sub: 'Help us tune how your community experience feels.',
    options: [
      { id: 'read', label: 'Mostly read & observe', icon: 'eye-outline' },
      { id: 'post', label: 'Share ideas & posts', icon: 'create-outline' },
      { id: 'chat', label: 'Jump into conversations', icon: 'chatbubbles-outline' },
      { id: 'build', label: 'Build & lead spaces', icon: 'home-outline' },
      { id: 'signals', label: 'Follow signals & trends', icon: 'pulse-outline' },
      { id: 'vote', label: 'Vote & shape decisions', icon: 'checkbox-outline' },
    ],
  },
];

interface RecommendedCommunity {
  id: string;
  name: string;
  displayName?: string;
  description?: string;
  icon?: string;
  memberCount?: number;
}

// ── Flow steps. Pane 0 is ALWAYS the username step. ──
const USERNAME_STEP = 0;
const INTEREST_OFFSET = 1;
const WALLET_STEP = SHOW_CRYPTO ? INTEREST_OFFSET + INTEREST_STEPS.length : -1;
const COMMUNITIES_STEP = INTEREST_OFFSET + INTEREST_STEPS.length + (SHOW_CRYPTO ? 1 : 0);
const PROFILE_STEP = COMMUNITIES_STEP + 1; // final: full profile setup
const TOTAL = PROFILE_STEP + 1;

// Minimal no-op wallet stub. SHOW_CRYPTO=false → the wallet pane never renders, so
// this never opens; it only needs to satisfy the unconditional persist-on-connect
// ref check below. (When crypto ships, swap this for the real useSafeWallet hook.)
const NO_WALLET = {
  ready: false,
  isConnected: false,
  address: null as string | null,
  open: () => {},
};

/**
 * Viewport-scaled sizes so every pane fits WITHOUT scrolling on all iPhones.
 * `compact` targets iPhone SE-class heights (≤700pt usable). The worst-case
 * pane (interests step 2: video header + 12 tiles in 4 rows) budgets to
 * ~620pt on SE and ~800pt on a 6.1" phone — both inside the viewport.
 */
function dims(height: number) {
  const compact = height <= 700;
  return {
    compact,
    pad: compact ? spacing.md : spacing.lg,
    videoH: compact ? 80 : Math.min(150, Math.max(100, Math.round(height * 0.15))),
    stepCount: compact ? 10.5 : 12,
    headline: compact ? 20 : 24,
    sub: compact ? 12.5 : 14,
    subMB: compact ? spacing.md : spacing.lg,
    gridGap: compact ? 8 : 10,
    tilePadV: compact ? 8 : 10,
    tileIcon: compact ? 26 : 32,
    tileGlyph: compact ? 15 : 17,
    tileFont: compact ? 10 : 11,
    tileLine: compact ? 13 : 14,
    fieldH: compact ? 44 : 50,
    commRows: compact ? 4 : 5,
    commPadV: compact ? 8 : 10,
    ctaH: compact ? 48 : 52,
  };
}

export default function OnboardingScreen() {
  // ── Layout / navigation ──
  // useNavigation is intentionally not used for advancing — finish() drives the
  // useAuth() gate instead — but is kept available for parity / future use.
  useNavigation();
  const insets = useSafeAreaInsets();
  const { width, height } = useWindowDimensions();
  const layout = getLayout(width);
  const maxW = layout !== 'phone' ? 560 : undefined;
  const containerStyle = maxW ? { maxWidth: maxW, alignSelf: 'center' as const, width: '100%' as const } : undefined;
  const d = dims(height);

  // ── Wizard state ──
  const [step, setStep] = useState(0); // 0..TOTAL-1
  const [selections, setSelections] = useState<Record<string, string[]>>({ vibe: [], interests: [], style: [] });

  // ── Username step ──
  const [username, setUsernameText] = useState('');
  const [currentUsername, setCurrentUsername] = useState('');
  const [usernameSavedFor, setUsernameSavedFor] = useState('');
  const [usernameLoaded, setUsernameLoaded] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState<
    'idle' | 'invalid' | 'checking' | 'available' | 'taken' | 'unknown' | 'saving' | 'error'
  >('idle');
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const checkSeq = useRef(0);

  // Prefill the username from the signed-in account. Always resolves the pane to
  // a usable state (even on a network failure) so step 0 can never hang blank.
  const loadUsername = useCallback(async () => {
    try {
      const res = await getMe();
      const dd: any = res?.data ?? res;
      const u = dd?.user ?? dd;
      const existing = typeof u?.username === 'string' ? u.username : '';
      if (existing) {
        setCurrentUsername(existing);
        setUsernameText(existing);
      }
    } catch {
      // Non-fatal — the user can still type a fresh username from scratch.
    } finally {
      setUsernameLoaded(true);
    }
  }, []);
  const loadedUsernameOnce = useRef(false);
  if (!loadedUsernameOnce.current) {
    loadedUsernameOnce.current = true;
    loadUsername();
  }

  // Debounced availability probe (preserved verbatim from the Expo flow).
  const usernameDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onUsernameChange = useCallback((text: string) => {
    const next = text.replace(/\s/g, '');
    setUsernameText(next);
    setUsernameError(null);
    const fmt = validateUsername(next);
    if (fmt) { setUsernameStatus(next.length === 0 ? 'idle' : 'invalid'); setUsernameError(next.length === 0 ? null : fmt); return; }
    if (currentUsername && next.toLowerCase() === currentUsername.toLowerCase()) {
      setUsernameStatus('available');
      return;
    }
    setUsernameStatus('checking');
    const seq = ++checkSeq.current;
    if (usernameDebounce.current) clearTimeout(usernameDebounce.current);
    usernameDebounce.current = setTimeout(async () => {
      const { available, unknown } = await checkUsernameAvailable(next, currentUsername);
      if (seq !== checkSeq.current) return; // superseded by a newer keystroke
      setUsernameStatus(unknown ? 'unknown' : available ? 'available' : 'taken');
    }, 450);
  }, [currentUsername]);

  // Final profile-setup pane (imperative save via ref; busy mirrored for CTA)
  const profilePaneRef = useRef<ProfileSetupHandle>(null);
  const [profileBusy, setProfileBusy] = useState(false);

  // Wallet (no-op stub; pane gated off when SHOW_CRYPTO=false)
  const wallet = NO_WALLET;

  // Communities
  const [recommended, setRecommended] = useState<RecommendedCommunity[]>([]);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [recsError, setRecsError] = useState(false);
  const [joined, setJoined] = useState<Record<string, boolean>>({});
  const [finishing, setFinishing] = useState(false);

  // ── Background video ──
  // One source per interest step, swapped by index. With react-native-video the
  // <RNVideo> repeats muted; if the lib is absent the header is a flat backdrop.
  const [videoIdx, setVideoIdx] = useState(0);
  const lastVideoIdx = useRef(0);
  function setVideo(idx: number) {
    if (idx === lastVideoIdx.current) return;
    lastVideoIdx.current = idx;
    setVideoIdx(idx);
  }

  // ── Selection helpers ──
  function toggle(stepId: string, optId: string) {
    setSelections((s) => {
      const cur = s[stepId] || [];
      const next = cur.includes(optId) ? cur.filter((x) => x !== optId) : [...cur, optId];
      return { ...s, [stepId]: next };
    });
  }

  const allInterests = useCallback(
    () => [...(selections.vibe || []), ...(selections.interests || []), ...(selections.style || [])],
    [selections]
  );

  // ── Wallet ──
  function handleWallet() {
    if (!wallet.ready) {
      Alert.alert('Connect Wallet', 'Wallet connect will be available in a future update. You can skip this step.');
      return;
    }
    try {
      wallet.open();
    } catch {
      Alert.alert('Wallet', 'Wallet connection is unavailable right now.');
    }
  }
  const persistedAddr = useRef<string | null>(null);
  if (wallet.isConnected && wallet.address && persistedAddr.current !== wallet.address) {
    persistedAddr.current = wallet.address;
    connectWalletAddress(wallet.address).catch(() => {});
  }

  // ── Recommended communities (hardened for empty-but-200 / Prisma-drift) ──
  const loadRecommended = useCallback(async () => {
    setLoadingRecs(true);
    setRecsError(false);
    const res = await recommendCommunities(allInterests());
    if (!res.success) {
      setRecommended([]);
      setRecsError(true);
      setLoadingRecs(false);
      return;
    }
    const dd: any = res.data ?? res;
    const list: RecommendedCommunity[] = Array.isArray(dd)
      ? dd
      : Array.isArray(dd?.communities)
        ? dd.communities
        : Array.isArray(dd?.data)
          ? dd.data
          : Array.isArray(dd?.items)
            ? dd.items
            : Array.isArray(dd?.results)
              ? dd.results
              : [];
    setRecommended(list);
    setLoadingRecs(false);
  }, [allInterests]);

  // Stable object key for a community. name/id can be null/undefined from a
  // drifted/empty-200 payload; never use an undefined value as an object key.
  const commKey = (c: RecommendedCommunity) => c?.name || c?.id || '';

  async function toggleJoin(c: RecommendedCommunity) {
    const key = commKey(c);
    if (!key || joined[key]) return;
    setJoined((j) => ({ ...j, [key]: true }));
    const res = await joinCommunity(key);
    if (!res.success) setJoined((j) => ({ ...j, [key]: false }));
  }

  // One-tap "join everything we suggested" — mirrors the per-row optimistic flow.
  function joinAll() {
    visibleRecs.forEach((c) => { const key = commKey(c); if (key && !joined[key]) toggleJoin(c); });
  }

  // Auto-suggest the top 3–5 (never scrolls). commRows is 4 (SE) / 5 (larger),
  // clamped to at least 3 so the step always shows a meaningful set.
  const suggestCount = Math.max(3, d.commRows);
  const visibleRecs = recommended.slice(0, suggestCount);
  const allJoined = visibleRecs.length > 0 && visibleRecs.every((c) => joined[commKey(c)]);
  // Labels for the interests we matched on (from the interest picker options).
  const suggestedTags = (() => {
    const picked = new Set(allInterests());
    const labels: string[] = [];
    for (const s of INTEREST_STEPS) {
      for (const o of s.options) {
        if (picked.has(o.id)) labels.push((o.label || '').split(' ')[0]);
      }
    }
    return labels;
  })();

  function goTo(ns: number) {
    setStep(ns);
    const interestIdx = ns - INTEREST_OFFSET;
    if (ns >= INTEREST_OFFSET && interestIdx < INTEREST_STEPS.length) setVideo(interestIdx);
    if (ns === COMMUNITIES_STEP) loadRecommended();
  }

  async function claimUsername(): Promise<boolean> {
    const candidate = username.trim().toLowerCase();
    const fmt = validateUsername(candidate);
    if (fmt) { setUsernameStatus('invalid'); setUsernameError(fmt); return false; }
    if (candidate === currentUsername.trim().toLowerCase() || candidate === usernameSavedFor) {
      return true;
    }
    setUsernameStatus('saving');
    setUsernameError(null);
    const res = await setUsername(candidate);
    if (res.success) {
      setCurrentUsername(candidate);
      setUsernameSavedFor(candidate);
      setUsernameStatus('available');
      return true;
    }
    const msg = res.error || 'Could not save that username. Please try again.';
    setUsernameStatus(/taken|use|exist/i.test(msg) ? 'taken' : 'error');
    setUsernameError(msg);
    return false;
  }

  // ── Navigation ──
  async function next() {
    if (step === USERNAME_STEP) {
      const ok = await claimUsername();
      if (!ok) return;
    }
    if (step === PROFILE_STEP) {
      // Final step: persist the profile pane, then finish. A partial failure
      // never traps the user — they can retry or continue into the app.
      const ok = await profilePaneRef.current?.save();
      if (ok === false) {
        Alert.alert(
          'Almost there',
          "Some of your profile couldn't be saved. You can fix it now or finish and edit it later.",
          [
            { text: 'Fix it', style: 'cancel' },
            { text: 'Finish anyway', onPress: () => finish() },
          ]
        );
        return;
      }
      finish();
      return;
    }
    goTo(step + 1);
  }

  function back() {
    if (step === 0) return;
    const ns = step - 1;
    setStep(ns);
    const interestIdx = ns - INTEREST_OFFSET;
    if (ns >= INTEREST_OFFSET && interestIdx < INTEREST_STEPS.length) setVideo(interestIdx);
  }

  // Complete onboarding: cache selections, tell the backend, then flip the
  // onboarding flag + nudge the gate so the root tree swaps to the app. The user
  // can't navigate back into the wizard (the AuthStack unmounts).
  async function finish() {
    setFinishing(true);
    try {
      await AsyncStorage.setItem('onboarding_selections', JSON.stringify(selections));
      await completeOnboarding(['username', 'interests', 'communities', 'profile', 'social']);
    } catch {}
    setFinishing(false);
    setOnboardingPending(false);
    notifyAuthGate();
  }

  // Skip NEVER blocks: any step (username + final included) can be skipped, and
  // skipping the last step finishes without saving the profile pane.
  function skipStep() {
    if (step < TOTAL - 1) {
      goTo(step + 1);
    } else {
      finish();
    }
  }

  // ── Derived pane flags ──
  const isInterestStep = step >= INTEREST_OFFSET && step < INTEREST_OFFSET + INTEREST_STEPS.length;
  const interestIdx = step - INTEREST_OFFSET;
  const usernameBlocks =
    step === USERNAME_STEP &&
    (username.trim().length === 0 ||
      usernameStatus === 'invalid' ||
      usernameStatus === 'taken' ||
      usernameStatus === 'checking' ||
      usernameStatus === 'saving' ||
      usernameStatus === 'error');
  const busy = finishing || profileBusy || usernameStatus === 'saving';
  const ctaDisabled = busy || usernameBlocks;

  return (
    <View style={[styles.root, { paddingTop: insets.top }]}>
      {/* Full-screen animated backdrop (gradient + drifting particles) */}
      <ParticleBackground theme="light" count={d.compact ? 5 : 7} intensity={0.9} />

      {/* Top: segmented progress bar + step counter + skip (momentum first) */}
      <View style={[styles.topBar, containerStyle, { paddingHorizontal: d.pad }]}>
        <View style={styles.progressCol}>
          <View style={styles.segments}>
            {Array.from({ length: TOTAL }).map((_, i) => (
              <View key={i} style={styles.segTrack}>
                <View style={[styles.segFill, i <= step && styles.segFillActive]} />
              </View>
            ))}
          </View>
          <Text style={[styles.progressLabel, tabularNums]}>Step {step + 1} of {TOTAL}</Text>
        </View>
        {!busy && (
          <TouchableOpacity onPress={skipStep} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }} accessibilityRole="button" accessibilityLabel="Skip this step">
            <Text style={styles.skip}>Skip</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Video header on interest steps (height scaled so the grid below never scrolls) */}
      {isInterestStep && (
        <View style={[styles.videoWrap, containerStyle, { height: d.videoH }]}>
          {RNVideo && (
            <RNVideo
              source={VIDEOS[videoIdx]}
              style={StyleSheet.absoluteFill}
              resizeMode="cover"
              repeat
              muted
              paused={false}
              playInBackground={false}
            />
          )}
          <LinearGradient
            colors={['transparent', 'rgba(232,225,242,0.7)', colors.bg]}
            style={StyleSheet.absoluteFill}
          />
        </View>
      )}

      {/* Fixed (non-scrolling) content — every pane is budgeted to fit the
          viewport; the keyboard shifts the pane up on the input steps. */}
      <KeyboardAvoidingView
        style={styles.contentFlex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={0}
      >
        <View
          style={[
            styles.content,
            containerStyle,
            { paddingHorizontal: d.pad, paddingTop: d.compact ? spacing.md : spacing.lg },
          ]}
        >
          {/* USERNAME (step 0) */}
          {step === USERNAME_STEP && (
            <GlassSurface variant="card" style={styles.paneGlass} contentStyle={[styles.paneContent, { padding: d.pad }]}>
              <ScreenHeading size="md" title="Choose your username" style={styles.paneHeading} />
              <Text style={[styles.sub, { fontSize: d.sub, marginBottom: d.subMB }]} numberOfLines={3}>
                This is how people find and @mention you across CRYB. You can change it later in settings.
              </Text>

              <View style={[
                styles.usernameField,
                { height: d.fieldH },
                usernameStatus === 'taken' || usernameStatus === 'invalid' || usernameStatus === 'error' ? styles.usernameFieldError : null,
                usernameStatus === 'available' ? styles.usernameFieldOk : null,
              ]}>
                <Text style={styles.usernameAt}>@</Text>
                <TextInput
                  style={styles.usernameInput}
                  value={username}
                  onChangeText={onUsernameChange}
                  placeholder={usernameLoaded ? 'username' : 'loading…'}
                  placeholderTextColor={colors.muted}
                  autoCapitalize="none"
                  autoCorrect={false}
                  autoComplete="username"
                  maxLength={32}
                  editable={usernameStatus !== 'saving'}
                  returnKeyType="done"
                />
                {usernameStatus === 'checking' || usernameStatus === 'saving' ? (
                  <ActivityIndicator size="small" color={colors.primary} />
                ) : usernameStatus === 'available' ? (
                  <Ionicons name="checkmark-circle" size={20} color={colors.success} />
                ) : (usernameStatus === 'taken' || usernameStatus === 'invalid' || usernameStatus === 'error') ? (
                  <Ionicons name="alert-circle" size={20} color={colors.error} />
                ) : null}
              </View>

              <Text style={[
                styles.usernameHint,
                (usernameStatus === 'taken' || usernameStatus === 'invalid' || usernameStatus === 'error') && styles.usernameHintError,
                usernameStatus === 'available' && styles.usernameHintOk,
              ]} numberOfLines={2}>
                {usernameError
                  ? usernameError
                  : usernameStatus === 'checking' ? 'Checking availability…'
                  : usernameStatus === 'saving' ? 'Saving…'
                  : usernameStatus === 'available' ? "Nice — that one's available."
                  : usernameStatus === 'taken' ? 'That username is already taken. Try another.'
                  : usernameStatus === 'unknown' ? "Couldn't verify right now — you can still continue."
                  : '3–32 characters. Letters, numbers, _ and - only.'}
              </Text>
            </GlassSurface>
          )}

          {/* INTEREST STEPS */}
          {isInterestStep && (
            <GlassSurface variant="card" style={styles.paneGlass} contentStyle={[styles.paneContent, { padding: d.pad }]}>
              <ScreenHeading size="md" title={INTEREST_STEPS[interestIdx].headline} style={styles.paneHeading} />
              <Text style={[styles.sub, { fontSize: d.sub, marginBottom: d.subMB }]} numberOfLines={2}>
                {INTEREST_STEPS[interestIdx].sub}
              </Text>
              <View style={[styles.grid, { gap: d.gridGap }]}>
                {INTEREST_STEPS[interestIdx].options.map((opt) => {
                  const sel = (selections[INTEREST_STEPS[interestIdx].id] || []).includes(opt.id);
                  return (
                    <TouchableOpacity
                      key={opt.id}
                      style={[styles.bubble, { paddingVertical: d.tilePadV }, sel && styles.bubbleSel]}
                      onPress={() => toggle(INTEREST_STEPS[interestIdx].id, opt.id)}
                      activeOpacity={0.8}
                      accessibilityRole="button"
                      accessibilityState={{ selected: sel }}
                      accessibilityLabel={`${opt.label}${sel ? ', selected' : ''}`}
                    >
                      <View style={[
                        styles.bubbleIcon,
                        { width: d.tileIcon, height: d.tileIcon, borderRadius: Math.round(d.tileIcon * 0.3) },
                        sel && styles.bubbleIconSel,
                      ]}>
                        <Ionicons name={opt.icon as any} size={d.tileGlyph} color={sel ? '#fff' : colors.primary} />
                      </View>
                      <Text
                        style={[styles.bubbleLabel, { fontSize: d.tileFont, lineHeight: d.tileLine }, sel && styles.bubbleLabelSel]}
                        numberOfLines={2}
                      >
                        {opt.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </GlassSurface>
          )}

          {/* WALLET LINKING (gated by SHOW_CRYPTO) */}
          {step === WALLET_STEP && (
            <GlassSurface variant="card" style={styles.paneGlass} contentStyle={[styles.paneContent, styles.centerStep, { padding: d.pad }]}>
              <Asset3D name="wallet" size={96} style={styles.walletAsset} />
              <Text style={[styles.headline, { fontSize: d.headline }]}>Connect a wallet</Text>
              <Text style={[styles.sub, { fontSize: d.sub, marginBottom: d.subMB }]}>
                Trade, hold, and use crypto features. You can do this later too.
              </Text>
              {wallet.isConnected && wallet.address ? (
                <View style={styles.walletConnected}>
                  <Ionicons name="checkmark-circle" size={20} color={colors.success} />
                  <Text style={styles.walletAddr}>
                    {wallet.address.slice(0, 6)}…{wallet.address.slice(-4)}
                  </Text>
                </View>
              ) : (
                <TouchableOpacity style={styles.walletBtn} onPress={handleWallet} activeOpacity={0.85} accessibilityRole="button" accessibilityLabel="Connect a wallet">
                  <LinearGradient
                    colors={[colors.primary, colors.primaryLight]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={styles.walletBtnGrad}
                  >
                    <Ionicons name="wallet" size={18} color="#fff" />
                    <Text style={styles.walletBtnText}>Connect Wallet</Text>
                  </LinearGradient>
                </TouchableOpacity>
              )}
            </GlassSurface>
          )}

          {/* SUGGEST COMMUNITIES BY INTEREST (top 3–5, one-tap join, no scroll) */}
          {step === COMMUNITIES_STEP && (
            <GlassSurface variant="card" style={styles.paneGlass} contentStyle={[styles.paneContent, { padding: d.pad }]}>
              <View style={styles.commHeadRow}>
                <View style={styles.commHeadText}>
                  <ScreenHeading size="md" title="Communities for you" style={styles.paneHeading} />
                  <Text style={[styles.sub, { fontSize: d.sub }]} numberOfLines={2}>
                    {suggestedTags.length > 0
                      ? `Matched to your interests. Tap to join — one tap, you're in.`
                      : 'Popular communities to get you started. Tap to join.'}
                  </Text>
                </View>
                {!loadingRecs && !recsError && visibleRecs.length > 1 && (
                  <TouchableOpacity
                    style={styles.joinAllBtn}
                    onPress={joinAll}
                    activeOpacity={0.8}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    accessibilityRole="button"
                    accessibilityLabel={allJoined ? 'All communities joined' : 'Join all suggested communities'}
                  >
                    <Ionicons
                      name={allJoined ? 'checkmark-done' : 'add'}
                      size={15}
                      color={colors.primary}
                    />
                    <Text style={styles.joinAllText}>{allJoined ? 'All joined' : 'Join all'}</Text>
                  </TouchableOpacity>
                )}
              </View>

              {/* Matched-interest chips (why these are shown) */}
              {suggestedTags.length > 0 && (
                <View style={[styles.tagRow, { marginBottom: d.subMB }]}>
                  {suggestedTags.slice(0, 4).map((t) => (
                    <View key={t} style={styles.tagChip}>
                      <Text style={styles.tagChipText}>{t}</Text>
                    </View>
                  ))}
                </View>
              )}

              {loadingRecs ? (
                <View style={styles.loadingWrap}><ActivityIndicator size="large" color={colors.primary} /></View>
              ) : recsError ? (
                <View style={styles.loadingWrap}>
                  <Asset3D name="error" size={96} style={{ marginBottom: spacing.sm }} />
                  <Text style={[styles.sub, { fontSize: d.sub, textAlign: 'center' }]}>Couldn't load recommendations. Check your connection and try again.</Text>
                  <TouchableOpacity style={styles.retryBtn} onPress={loadRecommended} activeOpacity={0.8} accessibilityRole="button" accessibilityLabel="Retry loading recommendations">
                    <Ionicons name="refresh" size={16} color={colors.primary} />
                    <Text style={styles.retryText}>Retry</Text>
                  </TouchableOpacity>
                </View>
              ) : visibleRecs.length === 0 ? (
                <View style={styles.loadingWrap}>
                  <Asset3D name="community" size={96} style={{ marginBottom: spacing.sm }} />
                  <Text style={[styles.sub, { fontSize: d.sub, textAlign: 'center' }]}>No recommendations yet — explore communities after setup.</Text>
                </View>
              ) : (
                visibleRecs.map((c, i) => {
                  const key = commKey(c);
                  const isJoined = !!key && joined[key];
                  return (
                  <GlassSurface
                    key={key || `rec-${i}`}
                    variant="pill"
                    sheen={false}
                    onPress={() => toggleJoin(c)}
                    style={styles.commRowGlass}
                    contentStyle={[styles.commRow, { paddingVertical: d.commPadV }]}
                  >
                    <View style={styles.commIcon}>
                      {c.icon ? (
                        <CachedImage source={{ uri: c.icon }} style={styles.commIconImg} />
                      ) : (
                        <Text style={styles.commIconLetter}>{(c.displayName || c.name || '?').charAt(0).toUpperCase()}</Text>
                      )}
                    </View>
                    <View style={styles.commInfo}>
                      <Text style={styles.commName} numberOfLines={1}>{c.displayName || c.name || 'Community'}</Text>
                      {!!c.description && <Text style={styles.commDesc} numberOfLines={1}>{c.description}</Text>}
                    </View>
                    <View style={[styles.followBtn, isJoined && styles.followBtnDone]}>
                      <Ionicons
                        name={isJoined ? 'checkmark' : 'add'}
                        size={14}
                        color={isJoined ? colors.primary : '#fff'}
                      />
                      <Text style={[styles.followText, isJoined && styles.followTextDone]}>
                        {isJoined ? 'Joined' : 'Join'}
                      </Text>
                    </View>
                  </GlassSurface>
                  );
                })
              )}
            </GlassSurface>
          )}

          {/* FINAL: SET UP YOUR PROFILE */}
          {step === PROFILE_STEP && (
            <GlassSurface variant="card" style={styles.paneGlass} contentStyle={[styles.paneContent, { padding: d.pad }]}>
              <ScreenHeading size="md" title="Set up your profile" style={styles.paneHeading} />
              <Text style={[styles.sub, { fontSize: d.sub, marginBottom: d.subMB }]} numberOfLines={2}>
                Photo, banner, name and socials — make it yours.
              </Text>
              <ProfileSetupPane ref={profilePaneRef} compact={d.compact} onBusyChange={setProfileBusy} />
            </GlassSurface>
          )}
        </View>
      </KeyboardAvoidingView>

      {/* Bottom CTA */}
      <View style={[styles.footer, { paddingBottom: insets.bottom + 12, paddingHorizontal: d.pad }, containerStyle]}>
        <View style={styles.footerRow}>
          {step > 0 && (
            <TouchableOpacity style={[styles.backBtn, { height: d.ctaH }]} onPress={back} activeOpacity={0.7} disabled={busy} accessibilityRole="button" accessibilityLabel="Go back">
              <Ionicons name="chevron-back" size={22} color={colors.ink} />
            </TouchableOpacity>
          )}
          <TouchableOpacity style={[styles.ctaBtn, ctaDisabled && styles.ctaBtnDisabled]} onPress={next} activeOpacity={0.85} disabled={ctaDisabled} accessibilityRole="button" accessibilityState={{ disabled: ctaDisabled }} accessibilityLabel={step < TOTAL - 1 ? 'Continue' : 'Get started'}>
            <LinearGradient
              colors={[colors.primary, colors.primaryLight]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={[styles.ctaGrad, { height: d.ctaH }]}
            >
              {busy ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.ctaText}>{step < TOTAL - 1 ? 'Continue' : 'Get started'}</Text>
              )}
            </LinearGradient>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.bg },
  topBar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: spacing.sm, zIndex: 10,
  },
  progressCol: { flex: 1, marginRight: spacing.md },
  segments: { flexDirection: 'row', gap: 4 },
  segTrack: { flex: 1, height: 4, borderRadius: radius.full, backgroundColor: 'rgba(108,92,231,0.14)', overflow: 'hidden' },
  segFill: { flex: 1, borderRadius: radius.full, backgroundColor: 'transparent' },
  segFillActive: { backgroundColor: colors.primary },
  progressLabel: { ...fonts.semibold, ...typographyScale.caption, color: colors.primary, marginTop: 6, textTransform: 'uppercase' },
  skip: { ...fonts.semibold, color: colors.muted, fontSize: 14 },
  videoWrap: { width: '100%', backgroundColor: '#1A1A2E', overflow: 'hidden' },
  contentFlex: { flex: 1 },
  content: { flex: 1, justifyContent: 'center' },
  paneGlass: { width: '100%' },
  paneContent: { borderRadius: radius.xl },
  headline: { ...fonts.bold, color: colors.ink, marginBottom: 6 },
  paneHeading: { marginBottom: 6 },
  sub: { ...fonts.regular, color: colors.ink2, lineHeight: 18 },
  grid: { flexDirection: 'row', flexWrap: 'wrap' },
  bubble: {
    width: '31%', alignItems: 'center', gap: 6, paddingHorizontal: 6,
    borderRadius: radius.lg, borderWidth: 2, borderColor: 'rgba(108,92,231,0.10)',
    backgroundColor: 'rgba(255,255,255,0.7)',
  },
  bubbleSel: { borderColor: colors.primary, backgroundColor: 'rgba(108,92,231,0.10)' },
  bubbleIcon: {
    backgroundColor: 'rgba(108,92,231,0.08)',
    alignItems: 'center', justifyContent: 'center',
  },
  bubbleIconSel: { backgroundColor: colors.primary },
  bubbleLabel: { ...fonts.semibold, color: colors.ink2, textAlign: 'center' },
  bubbleLabelSel: { color: colors.primary },
  usernameField: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: spacing.md, marginTop: spacing.sm,
    borderRadius: radius.lg, borderWidth: 2, borderColor: 'rgba(108,92,231,0.14)',
    backgroundColor: 'rgba(255,255,255,0.75)',
  },
  usernameFieldOk: { borderColor: colors.success },
  usernameFieldError: { borderColor: '#FF4757' },
  usernameAt: { ...fonts.semibold, color: colors.muted, fontSize: 17 },
  usernameInput: { flex: 1, ...fonts.semibold, color: colors.ink, fontSize: 16, paddingVertical: 0 },
  usernameHint: { ...fonts.regular, color: colors.muted, fontSize: 13, marginTop: spacing.sm, lineHeight: 18 },
  usernameHintOk: { color: colors.success },
  usernameHintError: { color: '#FF4757' },
  centerStep: { alignItems: 'center', paddingTop: spacing.xl },
  walletAsset: {
    marginTop: spacing.lg, marginBottom: spacing.lg,
  },
  walletBtn: { marginTop: spacing.lg, width: '100%' },
  walletBtnGrad: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
    paddingVertical: 16, borderRadius: radius.lg,
  },
  walletBtnText: { ...fonts.bold, color: '#fff', fontSize: 16 },
  walletConnected: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: spacing.lg, backgroundColor: 'rgba(45,181,93,0.1)', paddingVertical: 12, paddingHorizontal: 18, borderRadius: radius.lg },
  walletAddr: { ...fonts.semibold, color: colors.ink, fontSize: 15 },
  loadingWrap: { alignItems: 'center', justifyContent: 'center', paddingVertical: spacing.xxl, gap: spacing.md },
  retryBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingVertical: 10, paddingHorizontal: 20, borderRadius: radius.lg,
    borderWidth: 1.5, borderColor: colors.primary, backgroundColor: 'rgba(108,92,231,0.06)',
  },
  retryText: { ...fonts.semibold, color: colors.primary, fontSize: 14 },
  commHeadRow: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: spacing.sm },
  commHeadText: { flex: 1, minWidth: 0 },
  joinAllBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingVertical: 6, paddingHorizontal: 12, borderRadius: radius.full,
    borderWidth: 1.5, borderColor: colors.primary, backgroundColor: 'rgba(108,92,231,0.06)',
  },
  joinAllText: { ...fonts.semibold, color: colors.primary, fontSize: 12.5 },
  tagRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: spacing.sm },
  tagChip: { paddingVertical: 4, paddingHorizontal: 10, borderRadius: radius.full, backgroundColor: 'rgba(108,92,231,0.10)' },
  tagChipText: { ...fonts.semibold, color: colors.primary, fontSize: 11.5 },
  commRowGlass: { marginBottom: spacing.sm },
  commRow: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.md, paddingHorizontal: spacing.md,
  },
  commIcon: { width: 40, height: 40, borderRadius: 13, backgroundColor: 'rgba(108,92,231,0.1)', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  commIconImg: { width: 40, height: 40 },
  commIconLetter: { ...fonts.bold, color: colors.primary, fontSize: 17 },
  commInfo: { flex: 1, minWidth: 0 },
  commName: { ...fonts.semibold, color: colors.ink, fontSize: 14.5 },
  commDesc: { ...fonts.regular, color: colors.muted, fontSize: 12, marginTop: 1 },
  followBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingVertical: 7, paddingHorizontal: 12, borderRadius: radius.full, backgroundColor: colors.primary,
  },
  followBtnDone: { backgroundColor: 'rgba(108,92,231,0.12)' },
  followText: { ...fonts.semibold, color: '#fff', fontSize: 13 },
  followTextDone: { color: colors.primary },
  footer: { paddingTop: spacing.sm, backgroundColor: colors.bg },
  footerRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  backBtn: { width: 50, borderRadius: radius.lg, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.card, borderWidth: 1, borderColor: colors.cardBorder },
  ctaBtn: { flex: 1 },
  ctaBtnDisabled: { opacity: 0.6 },
  ctaGrad: { borderRadius: radius.lg, alignItems: 'center', justifyContent: 'center' },
  ctaText: { ...fonts.bold, color: '#fff', fontSize: 16 },
});
